import ExpandLess from "@mui/icons-material/ExpandLess";
import ExpandMore from "@mui/icons-material/ExpandMore";
import {
  Box,
  Button,
  Card,
  CardActionArea,
  Collapse,
  LinearProgress,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { StatusChip } from "components/StatusChip";
import {
  registrationIdIsStateWasteCode,
  registrationStateOmitsDot,
  registrationStateOmitsForm,
} from "encamp-shared/src/waste/constants";
import {
  RegistrationMatchWeights,
  registrationMatchWeightPercentages,
} from "encamp-shared/src/waste/registrationMatchWeights";
import {
  StateWasteStreamRegistrationStatus,
  WasteStreamRegistrationMatchesQuery,
} from "generated-graphql/graphql";
import { useState } from "react";

export type RegistrationMatch =
  WasteStreamRegistrationMatchesQuery["wasteStreamRegistrationMatches"][number];

// The weights the scorer actually used, not a restatement of them — so this
// table can't drift from `registrationMatchWeights`. The scoring-details table
// renders only the dimensions a state scores, so the column always sums to 100.
// A state can carry a fractional weight (TX: 37.5 / 9.375 / 3.125), so trailing
// zeros are trimmed rather than the number being rounded to an integer.
const weightLabel = (percent: number): string => `${Number(percent.toFixed(1))}%`;

/**
 * Whether this candidate matched on the state waste code — the stream carries
 * the registration's id among its state waste codes, for a state whose
 * registration id *is* a state waste code (TX). Drives the blue state-code chip
 * and its legend entry.
 */
function hasStateCodeHit(match: RegistrationMatch): boolean {
  const { registration } = match;
  const regStateId = registration.stateRegisteredId?.trim().toUpperCase();
  if (!registrationIdIsStateWasteCode(registration.state) || !regStateId) return false;
  return (match.stream.stateWasteCodes ?? [])
    .map((code) => code.trim().toUpperCase())
    .includes(regStateId);
}

const fmtList = (values: readonly string[] | null | undefined): string =>
  values && values.length > 0 ? values.join(", ") : "—";

const pct = (subscore: number): string => `${Math.round(subscore * 100)}%`;

/**
 * Names the dimension(s) a state's scoring-details footnote should call out.
 * Total over its inputs: when neither dimension is omitted there is nothing to
 * call out, so this returns "" rather than falling through to the form-code
 * wording. The caller only renders the footnote when `omitsDot || omitsForm`,
 * but this stays correct on its own terms in case that guard ever changes.
 */
function omittedDimensionsLabel(omitsDot: boolean, omitsForm: boolean): string {
  if (omitsDot && omitsForm) return "DOT and form code aren't";
  if (omitsDot) return "DOT isn't";
  if (omitsForm) return "Form code isn't";
  return "";
}

export interface MatchTabProps {
  matches: RegistrationMatch[];
  loading: boolean;
  selectedRegistrationId: string | null;
  onSelect: (registrationId: string) => void;
  /**
   * registrationId -> the name of the waste stream that registration already
   * owns. A candidate with an entry here is already related to a stream, so
   * it's shown with a MERGE chip and selecting it folds this stream into that
   * one (rather than adding a second link to the same registration).
   */
  relatedStreamNameByRegistrationId: Map<string, string>;
}

/**
 * The scored-dimension breakdown for one candidate, collapsed by default. Shows
 * each dimension the scorer weighs with both sides' values (the stream vs the
 * registration) and the per-dimension subscore, so a user can see exactly why a
 * candidate scored what it did. Lives outside the card's CardActionArea so
 * toggling it doesn't select the candidate.
 */
function ScoringDetails({ match }: { match: RegistrationMatch }) {
  const [open, setOpen] = useState(false);
  const { stream, registration, breakdown } = match;
  const omitsDot = registrationStateOmitsDot(registration.state);
  const omitsForm = registrationStateOmitsForm(registration.state);
  const stateCodeScored = registrationIdIsStateWasteCode(registration.state);
  const weightPct = registrationMatchWeightPercentages(registration.state);

  const dimensions: Array<{
    key: keyof RegistrationMatchWeights;
    label: string;
    streamValue: string;
    registrationValue: string;
    subscore: number;
    scored: boolean;
  }> = [
    {
      key: "stateCode",
      label: "State waste code",
      streamValue: fmtList(stream.stateWasteCodes),
      registrationValue: registration.stateRegisteredId || "—",
      subscore: breakdown.stateCode,
      scored: stateCodeScored,
    },
    {
      key: "code",
      label: "Federal codes",
      streamValue: fmtList(stream.federalWasteCodes),
      registrationValue: fmtList(registration.federalWasteCodes),
      subscore: breakdown.code,
      scored: true,
    },
    {
      key: "dot",
      label: "DOT / UN",
      streamValue: fmtList(stream.dotIds),
      registrationValue: registration.dotId || "—",
      subscore: breakdown.dot,
      scored: !omitsDot,
    },
    {
      key: "description",
      label: "Description",
      streamValue: stream.name || "—",
      registrationValue: registration.name || "—",
      subscore: breakdown.description,
      scored: true,
    },
    {
      key: "form",
      label: "Form code",
      streamValue: fmtList(stream.formCodes),
      registrationValue: fmtList(registration.formCodes),
      subscore: breakdown.form,
      scored: !omitsForm,
    },
  ];

  return (
    <Box sx={{ px: 2, pb: 1.5 }}>
      <Button
        size="small"
        variant="text"
        endIcon={open ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
        aria-expanded={open}
        onClick={(e) => {
          e.stopPropagation();
          setOpen((prev) => !prev);
        }}
        sx={{ textTransform: "none" }}
      >
        Scoring details
      </Button>
      <Collapse in={open} unmountOnExit>
        <Table size="small" sx={{ mt: 0.5 }}>
          <TableHead>
            <TableRow>
              <TableCell>Dimension</TableCell>
              <TableCell align="right">Weight</TableCell>
              <TableCell>This stream</TableCell>
              <TableCell>Registration</TableCell>
              <TableCell align="right">Match</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {dimensions
              .filter((dim) => dim.scored)
              .map((dim) => (
                <TableRow key={dim.key}>
                  <TableCell>{dim.label}</TableCell>
                  <TableCell align="right">{weightLabel(weightPct[dim.key])}</TableCell>
                  <TableCell sx={{ overflowWrap: "anywhere" }}>{dim.streamValue}</TableCell>
                  <TableCell sx={{ overflowWrap: "anywhere" }}>{dim.registrationValue}</TableCell>
                  <TableCell align="right">{pct(dim.subscore)}</TableCell>
                </TableRow>
              ))}
          </TableBody>
        </Table>
        {(omitsDot || omitsForm) && (
          <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5 }}>
            {omittedDimensionsLabel(omitsDot, omitsForm)} scored for{" "}
            {registration.state.toUpperCase()} registrations; the remaining dimensions carry their
            weight.
          </Typography>
        )}
      </Collapse>
    </Box>
  );
}

/** A single ranked candidate card. */
function MatchCard({
  match,
  isBestMatch,
  selected,
  onSelect,
  relatedStreamName,
}: {
  match: RegistrationMatch;
  isBestMatch: boolean;
  selected: boolean;
  onSelect: (registrationId: string) => void;
  relatedStreamName: string | undefined;
}) {
  const { registration } = match;
  // CLOSED registrations are shown (a matching-but-closed registration is worth
  // surfacing) but can't be linked, so the card is greyed and non-selectable.
  const isClosed = registration.status === StateWasteStreamRegistrationStatus.Closed;
  const stateId = registration.stateRegisteredId?.trim();
  // Aria label uses the bare id (assertions depend on it); the visible
  // title spells out "State ID" so the id isn't mistaken for a row index.
  const label = registration.stateRegisteredId ?? "New registration";
  const titleText = stateId ? `State ID ${stateId}` : "New registration";
  const stateCodeHit = hasStateCodeHit(match);
  // Same condition the dialog's confirm button uses to promise an update
  // submission: only a card that can actually be linked creates one, so a merge
  // target and a closed candidate say nothing about updates.
  const showUpdateNeeded = !isClosed && !relatedStreamName && match.extraStreamCodes.length > 0;

  return (
    <Card
      variant="outlined"
      // Closed candidates are de-emphasized, but raw 0.6 opacity on a dark
      // surface pushes text below a comfortable contrast — dim less in dark.
      sx={(theme) => ({
        borderColor: selected ? "primary.main" : "divider",
        borderWidth: selected ? 2 : 1,
        opacity: isClosed ? (theme.palette.mode === "dark" ? 0.8 : 0.6) : 1,
      })}
    >
      <CardActionArea
        onClick={() => {
          if (!isClosed) onSelect(registration.id);
        }}
        disabled={isClosed}
        aria-pressed={selected}
        aria-label={`Select registration ${label}`}
        sx={{ p: 2 }}
      >
        <Stack spacing={1}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" gap={1}>
            <Typography variant="subtitle2">
              {titleText}
              {registration.name ? ` — ${registration.name}` : ""}
            </Typography>
            <Stack direction="row" spacing={0.5} alignItems="center">
              {isClosed && <StatusChip color="default" label="Closed — not linkable" />}
              {relatedStreamName && !isClosed && <StatusChip color="warning" label="MERGE" />}
              {isBestMatch && <StatusChip color="info" label="Best match" />}
            </Stack>
          </Stack>

          {relatedStreamName && (
            <Typography variant="caption" color="text.secondary">
              Already linked to <strong>{relatedStreamName}</strong> — selecting this merges this
              waste stream into it.
            </Typography>
          )}

          <Stack direction="row" spacing={1} alignItems="center">
            <Box sx={{ flex: 1 }}>
              <LinearProgress
                variant="determinate"
                value={match.score}
                sx={{ height: 8, borderRadius: 4 }}
              />
            </Box>
            <Typography variant="body2" sx={{ minWidth: 72, textAlign: "right" }}>
              {match.score}% match
            </Typography>
          </Stack>

          <Stack direction="row" spacing={0.5} flexWrap="wrap" sx={{ rowGap: 0.5 }}>
            {match.sharedFederalCodes.map((code) => (
              <StatusChip key={`shared-${code}`} label={code} color="success" variant="outlined" />
            ))}
            {match.extraStreamCodes.map((code) => (
              <StatusChip key={`extra-${code}`} label={code} color="warning" variant="outlined" />
            ))}
            {stateCodeHit && registration.stateRegisteredId && (
              <StatusChip
                key="state-code"
                label={registration.stateRegisteredId}
                color="info"
                variant="outlined"
              />
            )}
          </Stack>

          <Typography variant="caption" color="text.secondary">
            DOT: {registration.dotId || "—"} · Form: {registration.formCodes.join(", ") || "—"}
          </Typography>

          {showUpdateNeeded && (
            <Typography variant="caption" color="warning.main">
              Update needed — the registration doesn&apos;t cover{" "}
              {match.extraStreamCodes.join(", ")}.
            </Typography>
          )}
        </Stack>
      </CardActionArea>

      <ScoringDetails match={match} />
    </Card>
  );
}

/**
 * Ranked candidate cards for the "Match existing" tab. Presentational: the
 * parent dialog owns the query, the selection state, and the Confirm action.
 * Renders exactly the array it's given, in score order — this never re-sorts
 * or re-filters. CLOSED candidates are shown greyed and non-selectable (they
 * can't be linked); candidates already linked to a stream are marked MERGE,
 * and the parent turns a confirm on one of those into the merge flow.
 */
export function MatchTab({
  matches,
  loading,
  selectedRegistrationId,
  onSelect,
  relatedStreamNameByRegistrationId,
}: MatchTabProps) {
  if (loading && matches.length === 0) {
    return <LinearProgress aria-label="Loading candidate registrations" />;
  }

  if (matches.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        No linkable registrations found at this facility.
      </Typography>
    );
  }

  // "Best match" marks the top *linkable* candidate. The server ranks by score
  // (closed rows included), so the highest-scoring row can be a non-linkable
  // closed one — the badge belongs on the first candidate a user can act on.
  const bestMatchRegistrationId = matches.find(
    (match) => match.registration.status !== StateWasteStreamRegistrationStatus.Closed
  )?.registration.id;

  // Only key the chip colors a user will actually see below.
  const showSharedKey = matches.some((match) => match.sharedFederalCodes.length > 0);
  const showExtraKey = matches.some((match) => match.extraStreamCodes.length > 0);
  const showStateCodeKey = matches.some(hasStateCodeHit);

  return (
    <Stack spacing={1.5}>
      {(showSharedKey || showExtraKey || showStateCodeKey) && (
        <Stack direction="row" spacing={2} flexWrap="wrap" alignItems="center" sx={{ rowGap: 0.5 }}>
          {showSharedKey && <ChipLegendKey color="success.main" label="Shared with registration" />}
          {showExtraKey && <ChipLegendKey color="warning.main" label="On this stream only" />}
          {showStateCodeKey && <ChipLegendKey color="info.main" label="State waste code match" />}
        </Stack>
      )}
      {matches.map((match) => (
        <MatchCard
          key={match.registration.id}
          match={match}
          isBestMatch={match.registration.id === bestMatchRegistrationId}
          selected={selectedRegistrationId === match.registration.id}
          onSelect={onSelect}
          relatedStreamName={relatedStreamNameByRegistrationId.get(match.registration.id)}
        />
      ))}
    </Stack>
  );
}

/** A color key for the waste-code chips shown on each candidate card. */
function ChipLegendKey({ color, label }: { color: string; label: string }) {
  return (
    <Stack direction="row" spacing={0.5} alignItems="center">
      <Box sx={{ width: 10, height: 10, borderRadius: "50%", bgcolor: color }} />
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
    </Stack>
  );
}
